/* OdmOsPlatformInstanceEditor.js */

"use strict";
const GameEditor = require("@gluenetworks/game_editor");
const _ = require("lodash");
const PLATFORM_CHUNK_SIZE = 250;

class OdmOsPlatformInstanceEditor extends GameEditor {
    constructor(
        logger,
        di,
        editorState,
        editorLayoutMgr,
        editorLayout,
        userSession,
        editorTabCmpnt,
        solutionsManager,
        steveIo,
        storageFactory,
        OdmOsPlatformEntity
    ) {
        super(logger, di, editorState.editorState, editorLayoutMgr);
        this._logger = logger;
        this._editorLayout = editorLayout;
        this._tabCmpnt = editorTabCmpnt;
        this._editorTabState = editorState;
        this._userSession = userSession;
        this._activeOrgId = userSession.activeOrganization.id;

        let solutionDefinition = solutionsManager.getActiveSolution();
        this._solutionName = solutionDefinition.name || "";
        let odmOsPlatformEditorViews = _.get(
            solutionDefinition,
            "odmOsPlatformEditor.viewNamespaces",
            []
        );
        let odmOsPlatformEditorActions = _.get(
            solutionDefinition,
            "odmOsPlatformEditor.actionNamespaces",
            []
        );

        let launchOptions = editorState.launchOptions;

        // May be needs to be dynamic
        this._odmOsPlatformTypeIcon = "osmanager.odm_os_platform_view";

        this._odmOsPlatformEntity = OdmOsPlatformEntity;
        this.currentEntityRemoved = false;
        this.currentEntityDeleteInProgress = false;
        this._storageFactory = storageFactory;
        this._userDataStorage = this._storageFactory.getRemoteStorageInstance(
            "view",
            "odm_os_platform_details"
        );
        this.setNamespaceGlobs(odmOsPlatformEditorViews.concat(odmOsPlatformEditorActions), true);
        this._instanceEditorState = {};
        this._platformProm = undefined;
        this._tabId = launchOptions.id || "Unknown";

        this.retardActions(true);

        // If we define the launching entity id
        const platformIds = [].concat(launchOptions.platformIds || []);

        this._initPlatformProm(platformIds);
        this._registerViewStateListener();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerViewStateListener() {
        this.on("ClearViewState", (this._onClearViewState = this._onClearViewState.bind(this)));
        this._cabinetCmpnt.on(
            "SelectView",
            (this._onSelectViewEditor = this._onSelectViewEditor.bind(this))
        );
    }

    _unRegisterCabinetListener() {
        this.removeListener("ClearViewState", this._onClearViewState);
        this._cabinetCmpnt.removeListener("SelectView", this._onSelectViewEditor);
    }

    _unRegisterViewStateListener() {
        this.removeAllListeners("ClearViewState");
    }

    _registerListenersOnPlatform() {
        this._odmOsPlatform.on(
            "NamePropertyUpdated",
            (this._onNamePropertyUpdated = this._onNamePropertyUpdated.bind(this))
        );
        this._odmOsPlatform.on(
            "EntityDeletedFromSystem",
            (this._onPlatformRemoved = this._onPlatformRemoved.bind(this))
        );
        this._odmOsPlatform.on(
            "DirtyStateChanged",
            (this._onDirtyStateChanged = this._onDirtyStateChanged.bind(this))
        );
    }

    _onSelectViewEditor(e) {
        this._userDataStorage.setData(e, "PE_active_view");
    }

    _unregisterListenersOnPlatform() {
        this._odmOsPlatform.removeListener("NamePropertyUpdated", this._onNamePropertyUpdated);
        this._odmOsPlatform.removeListener("EntityDeletedFromSystem", this._onPlatformRemoved);
        this._odmOsPlatform.removeListener("DirtyStateChanged", this._onDirtyStateChanged);
    }

    _onClearViewState() {
        _.delay(() => (this.editorState.viewState = undefined), 50);
    }

    _onDirtyStateChanged(newVal) {
        this._tabCmpnt.setDirty(newVal);
    }

    _onNamePropertyUpdated() {
        this.updateTab(this._odmOsPlatform.pristineProperties.name);
    }

    _onPlatformRemoved() {
        this.currentEntityRemoved = true;
        if (!this.currentEntityDeleteInProgress) {
            this.currentEntityRemoved = false;
            this.closeEditor(this.editorTabStateId, true);
        }
    }

    _initPlatformProm(platformIds) {
        if (platformIds.length === 1) {
            let platformId = platformIds[0];
            this._platformProm = Promise.resolve()
                .then(() => {
                    return this._odmOsPlatformEntity.retrieveOdmOsPlatform(
                        this._activeOrgId,
                        platformId,
                        {}
                    );
                })
                .then((platform) => {
                    this._odmOsPlatform = platform;
                    this._registerListenersOnPlatform();

                    // needs to be dynamic
                    this._odmOsPlatformTypeIcon = "osmanager.odm_os_platform_view";
                    this._tabCmpnt.setEditorIconName(this._odmOsPlatformTypeIcon);

                    return Promise.resolve();
                });
        } else if (platformIds.length > 1) {
            this._platformProm = this._initMultiplePlatforms(platformIds);
            this._tabCmpnt.setTitle(`Multi-Edit (${platformIds.length})`);
        }
    }

    getTabCmpnt() {
        return this._tabCmpnt;
    }

    getPlatform() {
        return this._odmOsPlatform;
    }

    get editorTabStateId() {
        return this._editorTabState.id;
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    whenPlatformIsReady() {
        if (!_.isUndefined(this._platformProm)) {
            return this._platformProm;
        } else {
            return Promise.reject("Can't load OdmOsPlatformInstanceEditor._platformProm");
        }
    }

    setInstanceEditorStateProperty(name, val) {
        _.set(this._instanceEditorState, name, val);
    }

    getInstanceEditorStateProperty(name) {
        return _.get(this._instanceEditorState, name);
    }

    updateTab(name) {
        this._tabCmpnt.setTitle(name);
    }

    updateTabIcon(iconName) {
        this._tabCmpnt.setEditorIconName(iconName);
    }

    closeEditor(id, force) {
        id = id || this._editorTabState.id;
        return super.closeEditor(id, force);
    }

    shutdown() {
        let logger = this._logger;
        this._unRegisterCabinetListener();
        return super
            .shutdown()
            .then(() => {
                this._unRegisterViewStateListener();
                return Promise.resolve();
            })
            .then(() => _.invoke(this._pagingFeed, "shutdown") || Promise.resolve())
            .then(() => {
                if (!_.isUndefined(this._odmOsPlatform)) {
                    this._unregisterListenersOnPlatform();
                    return this._odmOsPlatform.shutdown();
                } else {
                    return Promise.resolve();
                }
            })
            .then(() => {
                this._tabCmpnt = undefined;
                this._odmOsPlatform = undefined;
                this._registeredDetails = undefined;
                this._editorLayout = undefined;
                this._tabId = undefined;
                this._activeOrgId = undefined;
                this._platformProm = undefined;
                this.currentEntityRemoved = undefined;
                this.currentEntityDeleteInProgress = undefined;
                this._storageFactory = undefined;
                this._userDataStorage = undefined;
                this._pagingFeed = undefined;
                this._odmOsPlatformEntity = undefined;
            })
            .catch((err) => {
                logger.warn({error: err}, "OdmOsPlatformInstanceEditor.shutdown() error");
            });
    }

    clearPendingRelationshipsFor(key, entity) {
        let pendingRelationships = entity.getPendingRelationships();

        pendingRelationships =
            pendingRelationships &&
            pendingRelationships.filter((rel) => {
                if (rel.relName === key) {
                    return false;
                }
                return true;
            });

        entity.clearPendingRelationships(false);

        pendingRelationships.forEach((relation) => {
            entity._addPendingRelationship(
                relation.action,
                relation.relName,
                relation.rEntityType,
                relation.rEntityId
            );
        });
    }

    _initMultiplePlatforms(platformIds = []) {
        const chunkArray = (array, chunkSize) => {
            const chunks = [];
            for (let i = 0; i < array.length; i += chunkSize) {
                chunks.push(array.slice(i, i + chunkSize));
            }
            return chunks;
        };

        const chunkedplatformIds = chunkArray(platformIds, PLATFORM_CHUNK_SIZE);

        const loadPlatformChunk = (chunk) => {
            return this._odmOsPlatformEntity.loadOdmOsPlatforms(this._activeOrgId, {
                id: {$in: chunk},
            });
        };

        const platformPromises = chunkedplatformIds.map((chunk) => loadPlatformChunk(chunk));

        return Promise.all(platformPromises).then((results) => {
            const platforms = [];
            for (const result of results) {
                platforms.push(...result);
            }
            if (platforms.length > 0) {
                return platforms;
            } else {
                return Promise.reject("Requested ODM OS Platforms no longer exist");
            }
        });
    }

    generateCustomFieldJtype(customFieldDef) {
        const customFieldJtype = {};
        const customFieldId = customFieldDef.id;
        const type = customFieldDef.dataType.toLowerCase();
        const label = customFieldDef.label;
        const typeInfo = customFieldDef.typeInfo || {};

        customFieldJtype[customFieldId] = "";
        customFieldJtype[customFieldId + "@Display.label"] = label
            .split(" ")
            .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
            .join(" ");

        if (_.get(customFieldDef, "source", "") === "ControllerDiscoveryConfig") {
            customFieldJtype[customFieldId + "@Display.readOnly"] = true;
        }

        if (customFieldDef.description) {
            customFieldJtype[customFieldId + "@Display.description"] = customFieldDef.description;
        }

        switch (type) {
            case "number":
                customFieldJtype[customFieldId + "@Number"] = typeInfo.Number || {};
                break;
            case "string":
                customFieldJtype[customFieldId + "@String"] = typeInfo.String || {};
                break;
            case "boolean":
                customFieldJtype[customFieldId + "@value"] = false;
                break;
            case "enum": {
                const {ids = [], labels = [], multiSelect = false} = customFieldDef.typeInfo || {};
                let fieldJtypeDefinition;
                if (multiSelect) {
                    fieldJtypeDefinition = {
                        [`${customFieldId}`]: ids,
                        [`${customFieldId}@Display.tagList.labels`]: labels,
                        [`${customFieldId}@Display.tagList.minLineCount`]: 5,
                    };
                } else {
                    fieldJtypeDefinition = {
                        [`${customFieldId}`]: null,
                        [`${customFieldId}@Enum`]: ids,
                        [`${customFieldId}@Display.enum`]: labels,
                    };
                }
                Object.assign(customFieldJtype, fieldJtypeDefinition);
                break;
            }
        }
        return customFieldJtype;
    }
}

module.exports = OdmOsPlatformInstanceEditor;

OdmOsPlatformInstanceEditor.$inject = [
    "$teve.logger",
    "$teve.di",
    "editorLayout.editorState",
    "editorLayout.editorLayoutMgr",
    "EditorLayout.EditorLayout",
    "$teve.userSession",
    "editorLayout.editor.tabComponent",
    "$teve.solutionsManager",
    "$teve.io",
    "$teve.storageFactory",
    "$teve.entities.OdmOsPlatform",
];

// Optional convenient way to set a tab icon
OdmOsPlatformInstanceEditor.$editorIcon = "osmanager.odm_os_platform_view";
OdmOsPlatformInstanceEditor.$editorIconType = "base-icon-gl";
